import React from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import type { DashboardQuestionnaireCompletionItem } from '@/lib/api/dashboard';
import dashboardClientActivityUserIcon from '@/assets/images/dashboard-client-activity-user.svg';

import { CS_PRIMARY } from './dashboardHomeTokens';

export interface DashboardAnalyticsQuestionnaireCardProps {
  items?: DashboardQuestionnaireCompletionItem[];
  isLoading?: boolean;
  onViewAll?: () => void;
}

const getCompletionColor = (percent: number): string => {
  if (percent >= 75) return '#00bba7';
  if (percent >= 50) return '#ff9f43';
  return '#ffa2a2';
};

export const DashboardAnalyticsQuestionnaireCard: React.FC<DashboardAnalyticsQuestionnaireCardProps> = ({
  items = [],
  isLoading = false,
  onViewAll,
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 0,
      borderRadius: '14px',
      border: '1px solid #e5e7eb',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box component="img" src={dashboardClientActivityUserIcon} alt="" aria-hidden sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }} />
        <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#1e2939', letterSpacing: '-0.01em' }}>
          Questionnaire Completion
        </Typography>
      </Stack>
      <Button
        variant="text"
        size="small"
        onClick={onViewAll}
        endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 15 }} />}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 14,
          color: CS_PRIMARY,
          minWidth: 0,
          px: 0.5,
          '&:hover': { bgcolor: 'rgba(30, 174, 219, 0.06)' },
        }}
      >
        View All
      </Button>
    </Stack>

    <Stack spacing={1.25}>
      {isLoading && items.length === 0 && (
        <Typography sx={{ px: 1.75, py: 1.25, fontSize: 12, color: '#4a5565' }}>Loading questionnaire completion...</Typography>
      )}
      {!isLoading && items.length === 0 && (
        <Typography sx={{ px: 1.75, py: 1.25, fontSize: 12, color: '#4a5565' }}>No questionnaire completion data yet.</Typography>
      )}
      {items.map((row) => (
        <Stack
          key={row.questionnaireId || row.name}
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 1.75, py: 1.25, borderRadius: '14px' }}
        >
          <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#1a1a1a', width: 126, flexShrink: 0 }}>
            {row.name}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ flex: 1, height: 8, bgcolor: '#f3f4f6', borderRadius: 999, overflow: 'hidden', minWidth: 0 }}>
              <Box
                sx={{
                  width: `${row.percent}%`,
                  height: '100%',
                  bgcolor: getCompletionColor(row.percent),
                  borderRadius: 999,
                }}
              />
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#1a1a1a', width: 40, textAlign: 'right', flexShrink: 0 }}>
              {row.percent}%
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  </Paper>
);
